import { useCallback, useEffect, useState } from "react";

import { getMenus } from "services/menu/rest";
import { MenuListDto } from "services/menu/restModels";

export interface UseMenusResult {
    menus: MenuListDto[];
    loading: boolean;
    error: boolean;
    refetch: () => void;
}

/*
* Краткий список меню филиала — справочник: его читает диалог «Копировать в меню…», а дальше
* его же будет читать селект меню в шапке кабинета. Кешируем на уровне модуля (как
* useModifierSets): меню создают редко, а запрос иначе повторялся бы на каждом открытии диалога.
* Общий промис не даёт параллельным монтированиям плодить запросы.
* */
let cache: MenuListDto[] | null = null;
let inFlight: Promise<MenuListDto[] | null> | null = null;

const loadMenus = (): Promise<MenuListDto[] | null> => {
    if (cache) return Promise.resolve(cache);

    if (!inFlight) {
        inFlight = getMenus()
            .then((result) => {
                if (result) cache = result;
                return result;
            })
            .finally(() => {
                inFlight = null;
            });
    }

    return inFlight;
};

export const useMenus = (): UseMenusResult => {
    const [menus, setMenus] = useState<MenuListDto[]>(cache || []);
    const [loading, setLoading] = useState<boolean>(!cache);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);

    const refetch = useCallback(() => {
        cache = null;
        setReloadKey((key) => key + 1);
    }, []);

    useEffect(() => {
        // ignore-флаг: ответ после размонтирования не трогает стейт.
        let ignore = false;

        setLoading(true);
        setError(false);

        loadMenus()
            .then((result) => {
                if (ignore) return;
                if (result) {
                    setMenus(result);
                } else {
                    setError(true);
                    setMenus([]);
                }
            })
            .catch(() => {
                if (ignore) return;
                setError(true);
                setMenus([]);
            })
            .finally(() => {
                if (ignore) return;
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [reloadKey]);

    return { menus, loading, error, refetch };
};
